import React from 'react';
import { compileComponent } from './compileComponent';
import { UNIVERSAL_FUNCTIONS } from './universalFunctions';
import type { UxGenieFormEngineProps } from './types';

// Renders an AI-generated component with a formConfig AND caller-provided host
// function definitions. The caller is responsible for injecting any domain-
// specific functions (fetch calls, submit handlers, form runtime hooks, etc.).
// Universal utilities (traverseDotPath, formPrefillFunction) are always merged
// in — caller functions take precedence if there is a name collision.
//
// Standalone-safe: only imports React, compileComponent, and types.
export function UxGenieFormEngine({
  component,
  formConfig,
  hostFunctions,
  data,
}: UxGenieFormEngineProps) {
  // Merge universal utilities with caller-injected functions. Caller wins on
  // collision so domain-specific overrides of utilities are possible.
  const mergedHostFunctions = React.useMemo(
    () => ({ ...UNIVERSAL_FUNCTIONS, ...hostFunctions }),
    [hostFunctions]
  );

  React.useEffect(() => {
    window.FORM_CONFIG = formConfig;
    window.formConfig = formConfig;
  }, [formConfig]);

  const compilationResult = React.useMemo(() => {
    try {
      const ComponentToRender = compileComponent(component, {
        hostFunctions: mergedHostFunctions,
        data,
        formConfig,
      });
      return { ComponentToRender, error: null };
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Unknown error while compiling generated code.';
      return {
        ComponentToRender: () => (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            Error rendering component. {message}
          </div>
        ),
        error: message,
      };
    }
  }, [component, mergedHostFunctions, data, formConfig]);

  const { ComponentToRender, error } = compilationResult;

  return (
    <div className="h-full w-full min-w-0 overflow-auto rounded-[24px] bg-content1 p-4 lg:p-6">
      {error && (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
          Falling back to the error renderer because the generated component
          could not be executed.
        </div>
      )}
      <ComponentToRender />
    </div>
  );
}

export default UxGenieFormEngine;
